import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonPseudo,
  ResponseError,
  responseErrorsResolver,
  SignaturePopup,
  Popup,
  usePrevious,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useCreateCashCollectionSignatureMutation,
  useCreatePaymentOrderMutation,
} from './store/services';
import { useTransactionsUploadMutation } from '../transactions/store/services';
import { UploadResponse } from '../transactions/type';
import useGetAccountsAutocompleteList from './use-accounts-autocomplete';
import useGetPspPartnersAutocompleteList from '../../store/dictionaries/helpers/use-psp-partners-autocomplete';
import useGetPrimaryAccountsAutocompleteList from '../../store/dictionaries/helpers/use-primary-accounts-autocomplete';
import { PaymentOrder, PaymentOrderFormValues } from './types';
import CreateCashCollectionForm from '../../components/cash-collection/create';
import {
  useSendCodeMutation,
  useValidateCodeMutation,
} from '../../store/signature/service';
import { MfaToken, MfaType } from '../../store/types';
import { useCheckDocumentStringNameMutation } from '../../../../../store/service';

import ROUTES from '../../routes';

let timer: NodeJS.Timeout;

const CreateCashCollection = () => {
  const [panelsIsOpen, setPanelIsOpen] = useState(false);
  const [errorsMessage, setErrorMessage] = useState('');
  const [orderUploadId, setOrderUploadId] = useState('');
  const [receiptUploadId, setReceiptUploadId] = useState('');
  const [uploadedOrderFilesError, setUploadedOrderFilesError] = useState<
    string[]
  >([]);
  const [uploadedReceiptFilesError, setUploadedReceiptFilesError] = useState<
    string[]
  >([]);
  const [selectedUploadType, setSelectedUploadType] = useState<
    'order' | 'receipt'
  >();
  const [signaturePopupVisible, setSignaturePopupVisible] = useState(false);
  const [errorMessageSignature, setErrorMessageSignature] = useState('');
  const [createdTransactionId, setCreatedTransactionId] = useState('');
  const [orderUploadIsRequired, setOrderUploadIsRequired] = useState(false);
  const [receiptUploadIsRequired, setReceiptUploadIsRequired] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [
    uploadedComplianceDocumentErrors,
    setUploadedComplianceDocumentsErrors,
  ] = useState<{ name: string; error: string }[]>([]);
  const [uploadedReceiptDocumentsErrors, setUploadedReceiptDocumentsErrors] =
    useState<{ name: string; error: string }[]>([]);

  const uploadOrderInputRef = useRef<HTMLInputElement | null>(null);
  const uploadReceiptInputRef = useRef<HTMLInputElement | null>(null);

  const {
    transactionsDictionaries: { currencies, currenciesList, activePermissions },
    signatures: { mfaToken },
  } = useAppSelector((state) => state);

  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields },
    setError,
    reset,
    watch,
    setValue,
    clearErrors,
  } = useForm();

  const isDirty = Object.keys(dirtyFields).length !== 0;

  const pspPartnerId = watch('pspPartnerId');
  const currencyId = watch('currencyId');

  const prevPspPartnerId = usePrevious(pspPartnerId);
  const prevCurrencyId = usePrevious(currencyId);

  const [sendCode, { isLoading: sendCodeInProgress }] = useSendCodeMutation();
  const [validateCode, { isLoading: validateCodeInProgress }] =
    useValidateCodeMutation();
  const [createSignature, { isLoading: createSignatureInProgress }] =
    useCreateCashCollectionSignatureMutation();

  const [checkDocumentName, { isLoading: checkDocumentNameInProgress }] =
    useCheckDocumentStringNameMutation();
  const [upload, { isLoading: fileUploadInProgress }] =
    useTransactionsUploadMutation();
  const [create, { isLoading: requestInProgress }] =
    useCreatePaymentOrderMutation();

  const {
    autocompleteList: recipientAccounts,
    onSearch: onSearchRecipientAccounts,
    isLoading: recipientAccountsIsLoading,
    accountsList,
  } = useGetAccountsAutocompleteList(`&isClientAccount=true&closed=false`);

  const {
    autocompleteList: pspPartners,
    onSearch: onSearchPspPartners,
    isLoading: pspPartnersIsLoading,
  } = useGetPspPartnersAutocompleteList('&paymentsTypes=SWIFT', true);

  const {
    autocompleteList: pspPartnerAccounts,
    onSearch: onSearchPspPartnerAccounts,
    isLoading: pspPartnerAccountsIsLoading,
    nextPageIsLoading,
    pagination,
    onScroll,
  } = useGetPrimaryAccountsAutocompleteList(pspPartnerId, currencyId, true);

  const handleClose = useCallback(() => {
    setReceiptUploadId('');
    setUploadedOrderFilesError([]);
    setUploadedReceiptFilesError([]);
    setOrderUploadId('');
    setErrorMessage('');
    setSelectedUploadType(undefined);
    reset({});
    setSignaturePopupVisible(false);
    setCreatedTransactionId('');
    setErrorMessageSignature('');
    setUploadedComplianceDocumentsErrors([]);
    setUploadedReceiptDocumentsErrors([]);
    clearTimeout(timer);

    setTimeout(() => {
      navigate(`${ROUTES.CASH_COLLECTION}${search || ''}`);
    }, 100);
  }, []);

  const onSendCode = async () => {
    clearTimeout(timer);

    const response = (await sendCode({ mfa: MfaType.TOTP })) as {
      data: { data: MfaToken };
    };

    if (response && response.data && response.data.data) {
      setSignaturePopupVisible(true);

      const expireTime = Math.round(
        new Date(response.data.data.expiresAt).getTime() - Date.now(),
      );

      timer = setTimeout(() => {
        onSendCode();
      }, expireTime);
    }
  };

  const validateCodeAction = async (code: string) => {
    clearTimeout(timer);
    setErrorMessageSignature('');

    if (code) {
      const response = (await validateCode({
        mfaToken: mfaToken as string,
        totpToken: code,
      })) as {
        data?: { data: MfaToken };
        error: { data: { message: string; statusCode: number } };
      };

      if (response && response.data && response.data.data) {
        const createResponse = (await createSignature({
          id: createdTransactionId,
          body: {
            mfaToken: response.data.data.token,
            signedAt: new Date().toISOString(),
          },
        })) as {
          error: { data: { message: string }; status: number };
        };

        if (
          createResponse &&
          createResponse.error &&
          createResponse.error.data
        ) {
          if (createResponse.error?.status === 403) {
            setErrorMessageSignature('Access denied');
          } else {
            responseErrorsResolver({
              errors: createResponse.error as ResponseError,
              setError: () => {},
              setGlobalError: setErrorMessageSignature,
            });
          }
        } else {
          setSignaturePopupVisible(false);
          setErrorMessageSignature('');

          handleClose();
        }
      } else if (response && response.error && response.error.data) {
        if (response.error.data.statusCode === 401) {
          setErrorMessageSignature('Incorrect OTP code');
        } else {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError: () => {},
            setGlobalError: setErrorMessageSignature,
          });
        }
      }
    } else {
      setErrorMessageSignature('OTP Code should not be empty');
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setErrorMessage('');

    const body: PaymentOrderFormValues = {
      type: 'CASH_COLLECTION_SINGLE_OUTPUT',
      currencyId: values.currencyId,
      sum: Number(values.sum),
      organizationId: values?.organizationId,
      recepientAccountId: values?.recepientAccountId,
    };

    if (values.orderCreatedAt) {
      body.orderCreatedAt = values.orderCreatedAt;
    }

    if (values.pspPartnerId) {
      body.pspPartnerId = values.pspPartnerId;
    }

    if (values.pspPartnerAccountId) {
      body.pspPartnerAccountId = values.pspPartnerAccountId;
    }

    if (values.orderNumber) {
      body.orderNumber = values.orderNumber;
    }

    if (receiptUploadId) {
      body.receiptUploadId = receiptUploadId;
    }

    if (orderUploadId) {
      body.orderUploadId = orderUploadId;
    }

    if (!body.orderUploadId || !body.receiptUploadId) {
      if (!body.orderUploadId) {
        setOrderUploadIsRequired(true);
      }

      if (!body.receiptUploadId) {
        setReceiptUploadIsRequired(true);
      }
    } else {
      const response = (await create(body)) as {
        data?: { data: PaymentOrder };
        error: ResponseError;
      };

      if (response && response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      } else {
        setCreatedTransactionId(response?.data?.data?.id as string);
        onSendCode();
      }
    }
  });

  const onUpload = async (files: FileList, type: 'order' | 'receipt') => {
    setSelectedUploadType(type);

    if (type === 'order') {
      setUploadedOrderFilesError([]);
      setUploadedComplianceDocumentsErrors([]);
    } else {
      setUploadedReceiptFilesError([]);
      setUploadedReceiptDocumentsErrors([]);
    }

    const nextUploadedFilesErrors: string[] = [];
    const nextUploadedDocumentsErrors: { name: string; error: string }[] = [];

    const filesResponse = await Promise.all(
      Object.keys(files).map(async (file) => {
        const fileItem = files[file as any];

        const checkNameResponse = (await checkDocumentName({
          value: fileItem.name,
        })) as {
          error?: {
            data: {
              message?: {
                constraints: Record<string, string>;
              }[];
            };
          };
        };

        let nextFile;

        if (!checkNameResponse?.error) {
          const response = (await upload(files[file as any])) as unknown as {
            data?: UploadResponse[];
            error?: {
              originalStatus: number;
              data?: {
                message?: string;
              };
            };
            message?: string;
          };

          if (response && response.data) {
            nextFile = { ...response.data[0] };

            let fileName = fileItem?.name;

            if (
              response.data[0] &&
              response.data[0]?.mimeType?.includes('image')
            ) {
              fileName = `${fileItem?.name.slice(0, fileItem?.name.lastIndexOf('.'))}.jpg`;
            }

            nextFile.name = fileName;
          } else if (response.error) {
            if (response.error.originalStatus === 413) {
              nextUploadedFilesErrors.push(
                `${files[file as any].name} size too large`,
              );
            } else {
              // eslint-disable-next-line no-lonely-if
              if (response?.error?.data?.message) {
                nextUploadedFilesErrors.push(response?.error?.data?.message);
              }
            }
          }
        } else if (
          checkNameResponse?.error &&
          checkNameResponse?.error?.data?.message
        ) {
          const errorItem =
            checkNameResponse?.error?.data?.message[0]?.constraints;

          if (errorItem) {
            nextUploadedDocumentsErrors.push({
              name: files[file as any].name,
              error: Object.values(errorItem)[0],
            });
          }
        }

        return nextFile;
      }),
    );

    if (type === 'order') {
      setUploadedOrderFilesError(nextUploadedFilesErrors);
      setUploadedComplianceDocumentsErrors(nextUploadedDocumentsErrors);
    } else {
      setOrderUploadIsRequired(false);
      setUploadedReceiptFilesError(nextUploadedFilesErrors);
      setUploadedReceiptDocumentsErrors(nextUploadedDocumentsErrors);
    }

    if (filesResponse && filesResponse.length && filesResponse[0]) {
      const nextReceiptUploadId = filesResponse[0]?.id;

      if (type === 'order') {
        setOrderUploadId(nextReceiptUploadId);
        setOrderUploadIsRequired(false);
      } else {
        setReceiptUploadId(nextReceiptUploadId);
        setReceiptUploadIsRequired(false);
      }
    }

    if (uploadOrderInputRef && uploadOrderInputRef.current) {
      uploadOrderInputRef.current.value = '';
    }

    if (uploadReceiptInputRef && uploadReceiptInputRef.current) {
      uploadReceiptInputRef.current.value = '';
    }

    setSelectedUploadType(undefined);
  };

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (
      prevPspPartnerId !== undefined &&
      prevPspPartnerId !== null &&
      ((pspPartnerId && pspPartnerId !== prevPspPartnerId) ||
        (prevPspPartnerId && !pspPartnerId))
    ) {
      setValue('pspPartnerAccountId', '');
    }
  }, [pspPartnerId, prevPspPartnerId]);

  useEffect(() => {
    if (currencyId && prevCurrencyId && prevCurrencyId !== currencyId) {
      setValue('pspPartnerId', '');
      setValue('pspPartnerAccountId', '');
    }
  }, [currencyId, prevCurrencyId]);

  let content;

  if (
    activePermissions?.includes(
      PermissionEnum.PAYMENTS_ORDERS_BENEFICIARIES_CREATE_ACTION,
    )
  ) {
    content = (
      <CreateCashCollectionForm
        control={control}
        errors={errors}
        currency={currenciesList?.find((item) => item.id === currencyId)}
        currencies={currencies}
        recipientAccounts={recipientAccounts}
        recipientAccountsIsLoading={recipientAccountsIsLoading}
        onSearchRecipientAccounts={onSearchRecipientAccounts}
        pspPartners={pspPartners}
        pspPartnersIsLoading={pspPartnersIsLoading}
        onSearchPspPartners={onSearchPspPartners}
        pspAccountDisabled={!pspPartnerId}
        pspPartnerAccounts={pspPartnerAccounts}
        pspPartnerAccountsIsLoading={pspPartnerAccountsIsLoading}
        onSearchPspPartnerAccounts={onSearchPspPartnerAccounts}
        orderUploadId={orderUploadId}
        receiptUploadId={receiptUploadId}
        setReceiptUploadId={setReceiptUploadId}
        setOrderUploadId={setOrderUploadId}
        uploadOrderInputRef={uploadOrderInputRef}
        uploadReceiptInputRef={uploadReceiptInputRef}
        fileUploadInProgress={
          checkDocumentNameInProgress || fileUploadInProgress
        }
        onUpload={onUpload}
        uploadedOrderFilesError={uploadedOrderFilesError}
        uploadedReceiptFilesError={uploadedReceiptFilesError}
        selectedUploadType={selectedUploadType}
        orderUploadIsRequired={orderUploadIsRequired}
        receiptUploadIsRequired={receiptUploadIsRequired}
        setValue={setValue}
        accountsList={accountsList}
        clearErrors={clearErrors}
        uploadedComplianceDocumentErrors={uploadedComplianceDocumentErrors}
        uploadedReceiptDocumentsErrors={uploadedReceiptDocumentsErrors}
        pspPartnerAccountsNextPageIsLoading={nextPageIsLoading}
        pspPartnerAccountsPagination={pagination}
        pspPartnerAccountsOnScroll={onScroll}
      />
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <>
      <SidePanel
        title="Cash Collection"
        isOpen={panelsIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        type={SidePanelType.Transactions}
        errorSidepanel={
          errorsMessage?.includes(';')
            ? errorsMessage.split(';')
            : errorsMessage
        }
        sidepanelExpandedKey="cash-collection-panel"
        footer={
          <>
            <ButtonPrimary
              text={
                requestInProgress || sendCodeInProgress ? 'Loading...' : 'Save'
              }
              disabled={requestInProgress || sendCodeInProgress}
              onClick={onSubmit}
              bold
            />
            <ButtonPseudo text="Close" onClick={handleClose} bold />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <SignaturePopup
        signaturePopupVisible={signaturePopupVisible}
        validateCode={validateCodeAction}
        validateCodeInProgress={
          validateCodeInProgress || createSignatureInProgress
        }
        errorMessageSignature={errorMessageSignature}
        onSignatureCancelClick={handleClose}
      />
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('signature-popup') as HTMLElement}
      />
      <div id="signature-popup" />
    </>
  );
};

export default CreateCashCollection;
